import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useSites } from '../../../context/SiteContext';
import { useReportVisits } from '../../../hooks/useReportVisits';
import { buildVisitorType, splitNewReturn, summarizePeriod } from '../../../lib/analytics';
import { formatDuration, formatNumber, formatPercent } from '../../../lib/format';
import EmptyState from '../../../components/dashboard/EmptyState';
import PageHeader from '../../../components/dashboard/PageHeader';
import StatTable from '../../../components/dashboard/StatTable';
import ExportButton from '../../../components/dashboard/ExportButton';

export default function VisitorsPage() {
  const { currentSite } = useSites();
  const { ranged, humans, range } = useReportVisits(currentSite);
  const from = range.bounds.from.getTime();
  const stats = summarizePeriod(ranged, humans, from);
  const types = buildVisitorType(ranged, humans, from);
  const { news, olds } = splitNewReturn(ranged, humans, from);
  const newStats = summarizePeriod(news, humans, from);
  const oldStats = summarizePeriod(olds, humans, from);
  const total = types.reduce((sum, t) => sum + t.value, 0);
  const columns = ['类型', '访客数', '浏览量', '占比', '跳出率', '平均停留'];
  const rows = [
    [
      '新访客',
      formatNumber(newStats.uv),
      formatNumber(newStats.pv),
      total ? `${Math.round((newStats.uv / total) * 100)}%` : '0%',
      formatPercent(newStats.bounceRate),
      formatDuration(newStats.avgDuration),
    ],
    [
      '回访客',
      formatNumber(oldStats.uv),
      formatNumber(oldStats.pv),
      total ? `${Math.round((oldStats.uv / total) * 100)}%` : '0%',
      formatPercent(oldStats.bounceRate),
      formatDuration(oldStats.avgDuration),
    ],
  ];

  if (!currentSite) {
    return <EmptyState title="先添加一个站点" desc="添加站点后可区分新访客与回访客。" />;
  }

  return (
    <div>
      <PageHeader
        title="新老访客"
        desc="第一次到站记为新访客，历史上来过则记为回访客。"
        range={range}
        extra={<ExportButton filename={`${currentSite.domain}-visitors.csv`} columns={columns} rows={rows} />}
      />

      <div className="mt-6 grid grid-cols-2 xl:grid-cols-4 gap-3">
        {[
          ['独立访客', formatNumber(stats.uv)],
          ['新访客', formatNumber(stats.newUv)],
          ['回访客', formatNumber(stats.returnUv)],
          ['人均浏览', stats.pagesPerUv.toFixed(1)],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="text-xs text-foreground-500">{label}</div>
            <div className="mt-2 font-heading text-2xl font-bold text-foreground-950">{value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="text-sm font-semibold text-foreground-900">新老占比</h2>
          <div className="mt-4 h-72">
            {total === 0 ? (
              <p className="text-sm text-foreground-500">暂无访客数据</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={types} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100}>
                    <Cell fill="#E25A3C" />
                    <Cell fill="#2F9D6A" />
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200">
            <h2 className="text-sm font-semibold text-foreground-900">行为对比</h2>
          </div>
          <StatTable columns={columns} rows={rows} />
        </div>
      </div>
    </div>
  );
}
